import { useEffect, useRef, useState } from 'react'
import { C } from '@/lib/colors'

/* ---------- Sparkline ---------- */
export function Sparkline({
  data, width = 120, height = 34, color = C.cyan, fill = true, strokeWidth = 2,
  stretch = false, endDotColor,
}: {
  data: number[]; width?: number; height?: number; color?: string; fill?: boolean; strokeWidth?: number
  /** Fill the parent's width (measured — no viewBox distortion). */
  stretch?: boolean
  /** Endpoint dot color override (e.g. green = live data). */
  endDotColor?: string
}) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [measured, setMeasured] = useState<number | null>(null)
  useEffect(() => {
    if (!stretch || !wrapRef.current) return
    const el = wrapRef.current
    const ro = new ResizeObserver(() => setMeasured(el.clientWidth))
    ro.observe(el)
    setMeasured(el.clientWidth)
    return () => ro.disconnect()
  }, [stretch])

  const w = stretch ? (measured ?? width) : width
  if (!data || data.length < 2) {
    const empty = <svg width={w} height={height} />
    return stretch ? <div ref={wrapRef} className="spark-stretch">{empty}</div> : empty
  }
  const min = Math.min(...data), max = Math.max(...data)
  const span = max - min || 1
  const pad = strokeWidth + 1
  const x = (i: number) => pad + (i / (data.length - 1)) * (w - pad * 2)
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2)
  const pts = data.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`)
  const line = `M${pts.join(' L')}`
  const area = `${line} L${x(data.length - 1).toFixed(1)},${height} L${x(0).toFixed(1)},${height} Z`
  const svg = (
    <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} className="spark">
      {fill && <path d={area} fill={color} opacity={0.1} />}
      <path d={line} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(data.length - 1)} cy={y(data[data.length - 1])} r={strokeWidth + 0.5} fill={endDotColor ?? color} />
      {endDotColor && (
        <circle cx={x(data.length - 1)} cy={y(data[data.length - 1])} r={strokeWidth + 3} fill="none" stroke={endDotColor} strokeWidth={1} opacity={0.5} />
      )}
    </svg>
  )
  return stretch ? <div ref={wrapRef} className="spark-stretch">{svg}</div> : svg
}

/* ---------- Column bars (optional budget target line per column) ---------- */
export function ColumnBars({
  data, width = 280, height = 120, color = C.blue60, highlightColor = C.cyan,
}: {
  data: { label: string; value: number; budget?: number; highlight?: boolean }[]
  width?: number; height?: number; color?: string; highlightColor?: string
}) {
  if (!data.length) return null
  const all = data.flatMap((d) => [d.value, d.budget ?? 0])
  const max = Math.max(...all, 1)
  const padB = 18, padT = 6
  const plotH = height - padB - padT
  const gap = 8
  const bw = (width - gap * (data.length - 1)) / data.length
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="colbars" preserveAspectRatio="xMidYMid meet">
      {data.map((d, i) => {
        const bx = i * (bw + gap)
        const bh = (d.value / max) * plotH
        const by = padT + (plotH - bh)
        return (
          <g key={i}>
            <rect x={bx} y={by} width={bw} height={bh} rx={3} fill={d.highlight ? highlightColor : color} />
            {d.budget != null && (
              <line
                x1={bx - 1} x2={bx + bw + 1}
                y1={padT + (plotH - (d.budget / max) * plotH)}
                y2={padT + (plotH - (d.budget / max) * plotH)}
                stroke={C.navy} strokeWidth={1.5} strokeDasharray="3 2"
              />
            )}
            <text x={bx + bw / 2} y={height - 5} textAnchor="middle" fontSize="9" fill={C.platinum} fontFamily="var(--sans)">{d.label}</text>
          </g>
        )
      })}
    </svg>
  )
}

/* ---------- Donut gauge (single metric, 0–100) ---------- */
export function DonutGauge({
  value, size = 92, stroke = 10, color = C.cyan, track = C.fog, label, sub,
}: { value: number; size?: number; stroke?: number; color?: string; track?: string; label?: string; sub?: string }) {
  const r = (size - stroke) / 2
  const cx = size / 2, cy = size / 2
  const circ = 2 * Math.PI * r
  const pct = Math.max(0, Math.min(100, value))
  const dash = (pct / 100) * circ
  return (
    <div className="donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`} transform={`rotate(-90 ${cx} ${cy})`}
        />
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={size * 0.26} fontFamily="var(--serif)" fontWeight="300" fill={C.navy}>
          {label ?? `${Math.round(pct)}%`}
        </text>
      </svg>
      {sub && <div className="donut-sub label">{sub}</div>}
    </div>
  )
}

/* ---------- Funnel bars (WISE) ---------- */
export function FunnelBars({
  stages, color = C.blue, benchColor = C.platinum,
}: { stages: { name: string; value: number; benchmark?: number; display?: string }[]; color?: string; benchColor?: string }) {
  const max = Math.max(...stages.map((s) => Math.max(s.value, s.benchmark ?? 0)), 1)
  return (
    <div className="funnel">
      {stages.map((s, i) => (
        <div key={i} className="funnel-row">
          <div className="funnel-name">{s.name}</div>
          <div className="funnel-track">
            <div className="funnel-bar" style={{ width: `${(s.value / max) * 100}%`, background: color }} />
            {s.benchmark != null && (
              <div className="funnel-bench" style={{ left: `${(s.benchmark / max) * 100}%`, background: benchColor }} title={`Benchmark ${s.benchmark}`} />
            )}
          </div>
          <div className="funnel-val num">{s.display ?? s.value.toLocaleString()}</div>
        </div>
      ))}
    </div>
  )
}

/* ---------- Horizontal aging bars (delinquency 0–30 / 30–60 / 60–90 / 90+) ---------- */
export function AgingBars({
  buckets,
}: { buckets: { label: string; value: number; tone?: 'ok' | 'warn' | 'alert' | 'neutral' }[] }) {
  const max = Math.max(...buckets.map((b) => b.value), 1)
  const toneColor = { ok: C.ok, warn: C.warn, alert: C.alert, neutral: C.blue60 }
  return (
    <div className="aging">
      {buckets.map((b, i) => (
        <div key={i} className="aging-row">
          <div className="aging-label">{b.label}</div>
          <div className="aging-track">
            <div className="aging-bar" style={{ width: `${(b.value / max) * 100}%`, background: toneColor[b.tone ?? 'neutral'] }} />
          </div>
          <div className="aging-val num">{b.value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}</div>
        </div>
      ))}
    </div>
  )
}

/* ---------- 100% stacked bar (unit mix, occupancy by type) ---------- */
const SEGMENT_COLORS = [C.navy, C.blue, C.blue60, C.cyan, C.platinum, C.slate]
export function StackedBar({ segments, height = 12 }: { segments: { label: string; value: number; color?: string }[]; height?: number }) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1
  return (
    <div className="stacked">
      <div className="stacked-bar" style={{ height }}>
        {segments.map((s, i) => (
          <div key={i} className="stacked-seg" style={{ width: `${(s.value / total) * 100}%`, background: s.color ?? SEGMENT_COLORS[i % SEGMENT_COLORS.length] }} title={`${s.label}: ${s.value}`} />
        ))}
      </div>
      <div className="stacked-legend">
        {segments.map((s, i) => (
          <span key={i} className="stacked-leg-item">
            <span className="stacked-leg-dot" style={{ background: s.color ?? SEGMENT_COLORS[i % SEGMENT_COLORS.length] }} />
            {s.label} <b className="num">{Math.round((s.value / total) * 100)}%</b>
          </span>
        ))}
      </div>
    </div>
  )
}
